const PROJECT_SESSION_KEYS = [
  'project_id',
  'selected_project_id',
  'current_project_id',
  'active_project_id',
];
const PROJECT_SESSION_KEY_SET = new Set(PROJECT_SESSION_KEYS);
const JSON_START_CHARS = new Set(['{', '[', '"', '-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 't', 'f', 'n']);

const USER_SESSION_KEYS = {
  current_user_id: 'current_user_id',
  fallback_user_id: 'user_id',
  current_role: 'current_role',
};

export const parse_json_string = (value, fallback = {}) => parse_with_fallback(value, fallback);

function parse_with_fallback(value, fallback = {}) {
  try {
    return should_parse_json(value) ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

function should_parse_json(value = '') {
  return JSON_START_CHARS.has(String(value).trim().charAt(0));
}

function response_payload(response) {
  if (response?.data !== undefined && response?.data !== null) return response.data;
  return response ?? {};
}

function parse_nested_string(value) {
  return typeof value === 'string' ? parse_json_string(value) : value;
}

export function parse_response_data(response) {
  const raw_data = response_payload(response);
  if (typeof raw_data !== 'string') return raw_data;
  return parse_nested_string(parse_json_string(raw_data));
}

export function to_array(value) {
  return Array.isArray(value) ? value : [];
}

export function normalize_text(value) {
  return String(value || '').trim();
}

export function normalize_key(value) {
  return normalize_text(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

export function read_session_project_id() {
  return first_project_id(session_storage());
}

function session_storage() {
  return typeof window === 'undefined' ? null : window.sessionStorage;
}

function read_session_value(storage, key) {
  if (!PROJECT_SESSION_KEY_SET.has(key)) return null;
  return storage?.getItem(key);
}

function normalise_project_id(value) {
  return to_number(value);
}

function first_project_id(storage) {
  return PROJECT_SESSION_KEYS
    .map((key) => normalise_project_id(read_session_value(storage, key)))
    .find(Boolean) || null;
}

export function read_session_current_user_id() {
  if (typeof window === 'undefined') return null;
  return to_number(
    window.sessionStorage.getItem(USER_SESSION_KEYS.current_user_id)
    || window.sessionStorage.getItem(USER_SESSION_KEYS.fallback_user_id)
  );
}

export function read_session_current_role() {
  if (typeof window === 'undefined') return '';
  return normalize_text(window.sessionStorage.getItem(USER_SESSION_KEYS.current_role));
}

export function first_present(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '') ?? null;
}

export function get_entity_type_name(entity_type = {}) {
  const labels = [
    entity_type?.entity_type_name,
    entity_type?.entity_type_label,
    entity_type?.domain_name,
    entity_type?.domain,
    entity_type?.entity_type,
    entity_type?.name,
    entity_type?.label,
  ];
  return normalize_text(labels.find(Boolean));
}

export function object_label(value = {}) {
  if (typeof value !== 'object' || value === null) return normalize_text(value);

  const labels = [
    value?.status,
    value?.status_name,
    value?.workflow_status_name,
    value?.application_status,
    value?.entity_type_name,
    value?.entity_type_label,
    value?.domain_name,
    value?.name,
    value?.label,
  ];
  return normalize_text(labels.find(Boolean));
}

export function get_record_status(record = {}) {
  const statuses = [
    object_label(record?.application_status),
    record?.application_status_name,
    record?.current_status_name,
    record?.workflow_status_name,
    record?.status_name,
    object_label(record?.current_status),
    object_label(record?.workflow_status),
    object_label(record?.status),
    'New case',
  ];
  return normalize_text(statuses.find(Boolean));
}

export function is_open_case_status(status = '') {
  const closed_statuses = ['closed', 'complete', 'completed', 'cancelled', 'canceled', 'declined', 'rejected'];
  const status_parts = normalize_key(status).split('-');
  return !closed_statuses.some((status_key) => status_parts.includes(status_key));
}
